var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useState, useCallback, useContext, useEffect } from "react";
import axios from "axios";
import { createRequestError } from "./request";
import { RequestContext } from "./requestContext";
import { useMountedState, useRefFn } from "./utils";
export function useRequest(fn, options) {
    var getMountedState = useMountedState();
    var RequestConfig = useContext(RequestContext);
    var axiosInstance = (options === null || options === void 0 ? void 0 : options.instance) || RequestConfig.instance || axios;
    var customCreateReqError = RequestConfig.customCreateReqError;
    var _a = useState([]), sources = _a[0], setSources = _a[1];
    var hasPending = sources.length > 0;
    var removeCancelToken = useCallback(function (cancelToken) {
        if (getMountedState()) {
            setSources(function (prevSources) {
                return prevSources.filter(function (source) { return source.token !== cancelToken; });
            });
        }
    }, [getMountedState]);
    var _b = options || {}, onCompleted = _b.onCompleted, onError = _b.onError;
    var onCompletedRef = useRefFn(onCompleted);
    var onErrorRef = useRefFn(onError);
    var callFn = useRefFn(fn);
    var request = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var config = callFn.current.apply(callFn, args);
        var source = axios.CancelToken.source();
        var ready = function () {
            if (getMountedState()) {
                setSources(function (prevSources) { return __spreadArray(__spreadArray([], prevSources, true), [source], false); });
            }
            return axiosInstance(__assign(__assign({}, config), { cancelToken: source.token }))
                .then(function (response) {
                var _a;
                removeCancelToken(source.token);
                (_a = onCompletedRef.current) === null || _a === void 0 ? void 0 : _a.call(onCompletedRef, response.data, response);
                return [response.data, response];
            })
                .catch(function (err) {
                var _a;
                removeCancelToken(source.token);
                var error = customCreateReqError
                    ? customCreateReqError(err)
                    : createRequestError(err);
                (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, error);
                throw error;
            });
        };
        return {
            ready: ready,
            cancel: source.cancel,
        };
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [axiosInstance, customCreateReqError, getMountedState, removeCancelToken]);
    var clear = useCallback(function (message) {
        if (sources.length > 0) {
            sources.map(function (source) { return source.cancel(message); });
            if (getMountedState()) {
                setSources([]);
            }
        }
    }, [getMountedState, sources]);
    var clearRef = useRefFn(clear);
    var rtnClearFn = useCallback(function (message) { return clearRef.current(message); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    useEffect(function () {
        return clearRef.current;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return [request, { clear: rtnClearFn, hasPending: hasPending }];
}
